import{attachFieldKeyboard}from'../field-keyboard';
import { FieldTrailPanel } from './FieldTrailPanel';
import { fieldMoveInput } from '../field-movement-input';
/**
 * Field mode HUD (Matthew's 2026-10-21 gameplay direction): wander, spot, track,
 * then trap or hunt in a first-person bush mode. UI owns the HUD + trap selection;
 * movement/track/trap mechanics belong to Lane 1/4 via commands.
 * Trap ids match data/traps.json; descriptions are UI copy.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { Sheet } from './Sheet';
import { FieldCapture } from './FieldCapture';
import { IconBinocs, IconPaw, IconReserve, IconTag } from './icons';

const TRAP_TYPES = [
  { id: 'snare', name: 'Snare', mechanic: 'Quick set, small hoofstock, check often', activity: 'Set and wait' },
  { id: 'pitfall', name: 'Pitfall', mechanic: 'Slow dig, holds larger animals safely', activity: 'Digging minigame' },
  { id: 'box_trap', name: 'Box trap', mechanic: 'Baited, species-selective by bait', activity: 'Bait choice puzzle' },
  { id: 'drop_net', name: 'Drop net', mechanic: 'Covers a herd moment, needs timing', activity: 'Timing window' },
] as const;

/** Virtual joystick: drag within the well, emits field.move commands (throttled). */
function Joystick() {
  const { bridge } = useStore();
  const well = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const held = useRef<ReturnType<typeof fieldMoveInput> | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => { if(timer.current!==null)clearInterval(timer.current); }, []);
  const R = 44;

  const move = (e: { clientX: number; clientY: number }) => {
    const el = well.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let dx = e.clientX - (r.left + r.width / 2);
    let dy = e.clientY - (r.top + r.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > R) { dx = (dx / len) * R; dy = (dy / len) * R; }
    setKnob({ x: dx, y: dy });
    held.current = fieldMoveInput(dx / R, dy / R);
    if (timer.current === null) {
      bridge.send('field.move', held.current);
      timer.current = setInterval(() => { if(held.current)bridge.send('field.move',held.current); },90);
    }
  };
  const end = () => {
    if(timer.current!==null)clearInterval(timer.current);
    timer.current=null;held.current=null;
    setKnob({ x: 0, y: 0 });
    bridge.send('field.move', { dirX: 0, dirY: 0, run: false });
  };

  return (
    <div
      ref={well}
      class="joystick"
      role="application"
      aria-label="Move in field mode"
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); move(e); }}
      onPointerMove={(e) => { if (e.buttons) move(e); }}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <div class="joystick-knob" style={`transform:translate(${knob.x}px,${knob.y}px)`} />
    </div>
  );
}

const MG_LABEL = { tracking: 'Tracking trail', darting: 'Sedation window', capture: 'Net hold' } as const;
/** Active field minigames (ui.field.minigames: tracking, darting, capture), one progress bar each. */
function MinigameBars() {
  const { sim } = useStore();
  if (sim.minigames.length === 0) { return null; }
  return (
    <div class="field-minigames" role="status" aria-label="Active field activities">
      {sim.minigames.map((m) => {
        const pct = m.target > 0 ? Math.max(0, Math.min(100, Math.round((m.progress / m.target) * 100))) : 0;
        return (
          <div key={`${m.kind}:${m.targetId}`} class="mg-row">
            <span class="stealth-label">{MG_LABEL[m.kind]}</span>
            <span class="stealth-bar"><span class="stealth-fill" style={`width:${pct}%`} /></span>
            <span class="stealth-label">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

export function FieldHud() {
  const { mode, setMode, bridge, sim } = useStore();
  const [trapOpen, setTrapOpen] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [trailOpen,setTrailOpen]=useState(false);
  const [trackTarget,setTrackTarget]=useState('');
  const trackable=sim.fieldWorld?.animals.filter(a=>a.trackable)??[];
  const [trap, setTrap] = useState<string | null>(null);
  const [crouch, setCrouch] = useState(false);
  useEffect(()=>{if(mode!=='field')return;return attachFieldKeyboard(window,(type,payload)=>{bridge.send(type,payload);},()=>{const s=bridge.getState();return{heading:s.fieldWorld?.player.heading??0,crouched:s.fieldWorld?.player.crouched??false,zoomBp:s.fieldCapture?.zoomBp??10000};});},[mode,bridge]);
    if (mode !== 'field') return null;


  return (
    <>
      <div class="field-top">
        <span class="pill"><IconReserve style="width:14px;height:14px" />Savanna biome</span>
        <button class="btn btn-quiet field-exit" onClick={() => { setMode('ranch'); }}>Return to ranch</button>
      </div>
      <MinigameBars />
      {sim.field.goal && (
        <div class="field-goal"><span class="pill"><IconPaw style="width:13px;height:13px" />{sim.field.goal}</span></div>
      )}
      <div class="field-bottom">
        <div class="field-left">
          <div class="stealth-meter" aria-label="Stealth: noise and visibility">
            <div class="stealth-row">
              <span class="stealth-label">Noise</span>
              <span class="stealth-bar"><span class="stealth-fill" style={`width:${Math.round(sim.field.stealth.noise * 100)}%`} /></span>
            </div>
            <div class="stealth-row">
              <span class="stealth-label">Seen</span>
              <span class="stealth-bar"><span class="stealth-fill stealth-fill-seen" style={`width:${Math.round(sim.field.stealth.visibility * 100)}%`} /></span>
            </div>
          </div>
          <Joystick />
        </div>
        <div class="field-actions">
          <button class="btn btn-quiet" onClick={() => { if(sim.fieldWorld)bridge.send('field.spot', {sectorHeading:sim.fieldWorld.player.heading}); }}><IconBinocs />Spot</button>
          <select aria-label="Track field animal" value={trackTarget} onChange={e=>{setTrackTarget(e.currentTarget.value);}} style="max-width:164px;min-height:44px;background:var(--ui-parchment-soft);color:var(--ui-ink)"><option value="">Choose a trail...</option>{trackable.map(a=><option value={a.animalId}>{a.displayName} ({a.animalId})</option>)}</select><button class="btn btn-quiet" disabled={!trackable.some(a=>a.animalId===trackTarget)} onClick={() => { if(trackable.some(a=>a.animalId===trackTarget))bridge.send('field.track', {animalId:trackTarget}); }}><IconPaw />Track</button>
          <button class="btn btn-quiet" onClick={()=>{setTrailOpen(true);}}>Trail signs</button>
          <button class={`btn btn-quiet${sim.fieldWorld?.player.crouched ? ' on' : ''}`} onClick={() => { setCrouch(!crouch); bridge.send('field.crouch', { crouched: !(sim.fieldWorld?.player.crouched??crouch) }); }}>Crouch</button>
          <button class="btn btn-quiet" onClick={() => { setCapOpen(true); }}>Capture</button>
          <button class="btn btn-primary" onClick={() => { setTrapOpen(true); }}><IconTag />{trap ? `Trap: ${TRAP_TYPES.find((t) => t.id === trap)?.name ?? trap}` : 'Set trap'}<span class="trap-badge" aria-label={`${sim.field.trapsRemaining} traps left`}>{sim.field.trapsRemaining}</span></button>
        </div>
      </div>
      {trailOpen&&<FieldTrailPanel onClose={()=>{setTrailOpen(false);}}/>}
      {capOpen && <FieldCapture onClose={() => { setCapOpen(false); }} />}
      {trapOpen && (
        <Sheet label="Choose a trap" onClose={() => { setTrapOpen(false); }}>
          <div style="padding:6px 16px 18px">
            <div class="eyebrow eyebrow-rule">Trap selection</div>
            {TRAP_TYPES.map((t) => (
              <button
                key={t.id}
                class="card row"
                style={`border:var(--ui-border);width:100%;text-align:left;cursor:pointer;background:var(--ui-parchment-soft);margin-top:8px;${trap === t.id ? 'outline:2px solid var(--ui-amber);' : ''}`}
                onClick={() => { setTrap(t.id); }}
              >
                <span class="row-main">
                  <div class="row-value">{t.name}</div>
                  <div class="row-label">{t.mechanic}</div>
                  <div class="row-label" style="color:var(--ui-amber-deep)">{t.activity}</div>
                </span>
              </button>
            ))}
            <button
              class="btn btn-primary" style="width:100%;margin-top:12px"
              disabled={!trap}
              onClick={() => { bridge.send('field.trap.place', { trapTypeId: trap }); setTrapOpen(false); }}
            >Place trap</button>
            <p style="font-size:11.5px;color:var(--ui-ink-soft);margin-top:8px;text-align:center">
              Sets the trap where you stand. Up to four can be armed at once.
            </p>
          </div>
        </Sheet>
      )}
    </>
  );
}
